import React, { useRef, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Chip,
  Button,
  Box,
  IconButton,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import QrScanner from "qr-scanner";
import CloseIcon from "@mui/icons-material/Close";
import InventoryIcon from "@mui/icons-material/Inventory2Outlined";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import { useTheme } from "@mui/material/styles";
import PrintingVM from "../viewmodel/PrintingVM";
import AxiosWrapper from "../../../basecomp/base/AxiosWrapper";
// import successSound from "../../asset/success.mp3";
// import errorSound from "../../asset/error.mp3";

// Self-hosted worker path — avoids relying on import.meta.url / bundler
// support for it entirely. After `npm install qr-scanner`, copy
// node_modules/qr-scanner/qr-scanner-worker.min.js into your /public folder
// (add a postinstall script so it survives npm installs), then:
QrScanner.WORKER_PATH = "/qr-scanner-worker.min.js";

export default function PutAwayModal({
  open,
  onClose,
  materialNo,
  storageLocation,
  binLocation,
  onSuccess,
  handleToastOpen
}) {
  const videoRef = useRef(null);
  const qrScannerRef = useRef(null);
  const successAudioRef = useRef(null);
  const errorAudioRef = useRef(null);
  const resultTimeoutRef = useRef(null);

  const { bulkLocation } = PrintingVM({
    material: materialNo,
    bin_location: binLocation,
    storage_loc: storageLocation,
    popupType: "ware",
  });

  // 'loading' | 'select' | 'scan'
  const [stage, setStage] = useState("loading");
  // 'scanning' | 'valid' | 'invalid' | 'denied'
  const [scanState, setScanState] = useState("scanning");
  const [pendingList, setPendingList] = useState([]);
  const [selectedPutAway, setSelectedPutAway] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    successAudioRef.current = new Audio("https://cdn.pixabay.com/audio/2026/04/29/audio_02ddb885e5.mp3");
    errorAudioRef.current = new Audio("https://cdn.pixabay.com/audio/2023/05/15/audio_d1c0032f6e.mp3");

    successAudioRef.current.onerror = (e) => {
      console.error("Success audio load failed", e);
    };
    errorAudioRef.current.onerror = (e) => {
      console.error("Error audio load failed", e);
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    const fetchPendingPutAway = async () => {
      setStage("loading");
      setSelectedPutAway(null);
      setPendingList([]);
      try {
        const response = await AxiosWrapper.axiosGetCallWithToken(
          `${process.env.REACT_APP_BE_URL}/gsone/pendingPutAway?material_no=${materialNo}&storage_loc=${storageLocation}`
        );
        const list = response?.data ?? [];

        if (list.length === 1) {
          // only one pending record -> select it silently, no UI shown
          setSelectedPutAway(list[0]);
          setStage("scan");
        } else if (list.length > 1) {
          setPendingList(list);
          setStage("select");
        } else {
          // nothing pending -> fall back to scanning without a specific record
          setPendingList([]);
          onClose();
          handleToastOpen("error", "No Material to Put Away")
        }
      } catch (e) {
        console.error("Failed to fetch pending put away", e);
        handleToastOpen("error", "No Material to Put Away")
        setStage("scan");
        onClose();
      }
    };

    fetchPendingPutAway();
  }, [open, materialNo, storageLocation]);

  const playSound = (type) => {
    const audio = type === "success" ? successAudioRef.current : errorAudioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    audio.play().catch((err) => {
      console.error("Sound play failed:", err.name, err.message);
    });
  };

  // Fully release the camera. Only call this when actually done scanning
  // (modal closed / leaving scan stage) — NOT between individual scan attempts.
  const teardownScanner = () => {
    if (!qrScannerRef.current) return;
    qrScannerRef.current.stop();
    qrScannerRef.current.destroy();
    qrScannerRef.current = null;
  };

  // Create the scanner once per scan session and keep the camera stream
  // alive for the whole session. Re-requesting getUserMedia on every scan
  // attempt (the old behaviour) is what made this flaky on iOS.
  useEffect(() => {
    if (!open || stage !== "scan") return;
    if (qrScannerRef.current || !videoRef.current) return;

    let cancelled = false;

    const scanner = new QrScanner(
      videoRef.current,
      (result) => handleDecode(result.data),
      {
        preferredCamera: "environment",
        maxScansPerSecond: 10,
        highlightScanRegion: false,
        highlightCodeOutline: false,
      }
    );
    qrScannerRef.current = scanner;

    scanner
      .start()
      .then(() => {
        if (!cancelled) setScanState("scanning");
      })
      .catch((e) => {
        console.log(e);
        scanner.destroy();
        qrScannerRef.current = null;
        if (!cancelled) setScanState("denied");
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stage]);

  // Release the camera when the scan session actually ends.
  useEffect(() => {
    if (open && stage === "scan") return;
    teardownScanner();
  }, [open, stage]);

  useEffect(() => {
    return () => teardownScanner();
  }, []);

  const resetAndClose = () => {
    clearTimeout(resultTimeoutRef.current);
    setScanState("scanning");
    setStage("loading");
    setSelectedPutAway(null);
    setPendingList([]);
    onClose();
  };

  const handleSelectPutAway = (record) => {
    setSelectedPutAway(record);
    setStage("scan");
  };

  const handleDecode = (text) => {
    // Pause decoding but keep the stream alive (pass false) — avoids
    // re-acquiring the camera between attempts.
    qrScannerRef.current?.pause(false);

    let valid = false;
    let bin;
    try {
      const url = new URL(text);
      const storage = url.searchParams.get("storage_loc");
      bin = url.searchParams.get("storage_bin");
      valid = storage === storageLocation && (bin === binLocation || bin === bulkLocation);
    } catch {
      valid = false;
    }

    if (valid) {
      playSound("success");
      setScanState("valid");
      let location_type;
      if (bin === binLocation) {
        location_type = "Bin Location";
      } else if (bin === bulkLocation) {
        location_type = "Bulk Location";
      }
      onSuccess(location_type, selectedPutAway);
      resultTimeoutRef.current = setTimeout(() => {
        setScanState("scanning");
        onClose();
      }, 1100);
    } else {
      playSound("error");
      setScanState("invalid");
      resultTimeoutRef.current = setTimeout(() => {
        setScanState("scanning");
        qrScannerRef.current?.start(); // resumes decoding, camera was never stopped
      }, 1400);
    }
  };

  const statusConfig = {
    scanning: { label: "Scan Location", color: "#1976d2" },
    valid: { label: "Valid Location", color: "#2e7d32" },
    invalid: { label: "Invalid Location", color: "#d32f2f" },
    denied: { label: "Permission Needed", color: "#d32f2f" },
  };
  const active = statusConfig[scanState];

  return (
    <Dialog
      open={open}
      fullWidth
      fullScreen={isMobile}
      maxWidth="xs"
      onClose={resetAndClose}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 4, overflow: "hidden" } }}
    >
      <style>{`
        @keyframes circleDraw { to { stroke-dashoffset: 0; } }
        @keyframes markDraw { to { stroke-dashoffset: 0; } }
        @keyframes shake {
          10%, 90% { transform: translateX(-2px); }
          20%, 80% { transform: translateX(4px); }
          30%, 50%, 70% { transform: translateX(-8px); }
          40%, 60% { transform: translateX(8px); }
        }
        @keyframes popIn {
          0% { opacity: 0; transform: scale(.85); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>

      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "#1976d2",
          color: "#fff",
          py: 1.5,
        }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          Put Away
        </Typography>
        <IconButton size="small" sx={{ color: "#fff" }} onClick={resetAndClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2, bgcolor: "#f5f7fa" }}>
        {/* info card */}
        <Box
          sx={{
            borderRadius: 3,
            p: 2,
            mb: 2.5,
            bgcolor: "#fff",
            border: "1px solid #e8ebf0",
            boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
          }}
        >
          <Box mb={1.75}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.06em",
                color: "#94a3b8",
                textTransform: "uppercase",
                mb: 0.3,
              }}
            >
              Material
            </Typography>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }}>
              {materialNo}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: bulkLocation ? "repeat(3, 1fr)" : "repeat(2, 1fr)",
              gap: 1,
            }}
          >
            <LocationTag label="Storage" value={storageLocation} accent="#2563eb" />
            <LocationTag label="Bin" value={binLocation} accent="#0891b2" />
            {/* {bulkLocation && <LocationTag label="Bulk" value={bulkLocation} accent="#7c3aed" />} */}
          </Box>
        </Box>

        {stage === "loading" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              py: 6,
            }}
          >
            <CircularProgress size={32} />
            <Typography sx={{ fontSize: 13, color: "#64748b", fontWeight: 600 }}>
              Checking pending put-away...
            </Typography>
          </Box>
        )}

        {stage === "select" && (
          <PendingSelectList list={pendingList} onSelect={handleSelectPutAway} />
        )}

        {stage === "scan" && (
          <>
            {/* scanner / result area — video stays mounted the whole scan
                session; overlays sit on top instead of replacing it, so the
                camera stream is never torn down between attempts */}
            <Box
              sx={{
                position: "relative",
                width: "100%",
                aspectRatio: "1 / 1",
                maxHeight: isMobile ? "70vh" : 340,
                borderRadius: 3,
                overflow: "hidden",
                border: `2px solid ${active.color}`,
                mb: 2,
                bgcolor: "#000",
                transition: "border-color .3s ease",
              }}
            >
              <video
                ref={videoRef}
                muted
                playsInline
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />

              {scanState !== "scanning" && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    bgcolor: "rgba(0,0,0,0.92)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {scanState === "valid" && (
                    <ResultAnimation type="success" text="Location Confirmed" />
                  )}
                  {scanState === "invalid" && (
                    <ResultAnimation type="error" text="Try Scanning Again" />
                  )}
                  {scanState === "denied" && <PermissionDeniedView />}
                </Box>
              )}
            </Box>

            <Box display="flex" justifyContent="center" mb={2}>
              <Chip
                label={active.label}
                sx={{
                  fontSize: 14,
                  px: 2,
                  py: 2.2,
                  fontWeight: 600,
                  color: "#fff",
                  bgcolor: active.color,
                  transition: "background-color .3s ease",
                }}
              />
            </Box>
          </>
        )}

        <Button
          fullWidth
          variant="contained"
          color="error"
          size="large"
          onClick={resetAndClose}
          sx={{ borderRadius: 2, py: 1.2 }}
        >
          Cancel
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function PendingSelectList({ list, onSelect }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.06em",
          color: "#fa0404",
          textTransform: "uppercase",
          mb: 1,
        }}
      >
        Multiple pending put-aways — select one
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, maxHeight: 340, overflowY: "auto" }}>
        {list.map((item, idx) => (
          <Box
            key={item.id ?? idx}
            onClick={() => onSelect(item)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              borderRadius: 2.5,
              p: 1.5,
              bgcolor: "#fff",
              border: "1px solid #e2e8f0",
              cursor: "pointer",
              transition: "border-color .15s ease, box-shadow .15s ease",
              "&:hover": {
                borderColor: "#1976d2",
                boxShadow: "0 1px 6px rgba(25,118,210,0.15)",
              },
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                bgcolor: "#e8f0fe",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <InventoryIcon sx={{ fontSize: 18, color: "#1976d2" }} />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.3 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                  Qty: {item.print_qty}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>
                  {

                    new Date(Number(item.printed_date)).toLocaleString("en-IN", {
                      timeZone: localStorage.getItem("time_zone"),
                    })}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                Printed by {item.name}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function LocationTag({ label, value, accent }) {
  return (
    <Box
      sx={{
        borderRadius: 2,
        p: 1,
        bgcolor: `${accent}0d`,
        border: `1px solid ${accent}26`,
        textAlign: "center",
      }}
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.05em",
          color: accent,
          textTransform: "uppercase",
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
        {value}
      </Typography>
    </Box>
  );
}

function PermissionDeniedView() {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.25,
        px: 3,
        textAlign: "center",
      }}
    >
      <VideocamOffIcon sx={{ fontSize: 38, color: "#f87171" }} />
      <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
        Permission Denied
      </Typography>
      <Typography sx={{ color: "#cbd5e1", fontSize: 12.5, lineHeight: 1.4 }}>
        Please allow camera access.
      </Typography>
    </Box>
  );
}

function ResultAnimation({ type, text }) {
  const isSuccess = type === "success";
  const color = isSuccess ? "#2e7d32" : "#d32f2f";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1.5,
        animation: "popIn .25s ease",
      }}
    >
      <svg
        width="88"
        height="88"
        viewBox="0 0 96 96"
        style={!isSuccess ? { animation: "shake .4s ease" } : undefined}
      >
        <circle
          cx="48"
          cy="48"
          r="44"
          fill="none"
          stroke={color}
          strokeWidth="4"
          strokeDasharray="276"
          strokeDashoffset="276"
          style={{ animation: "circleDraw .45s ease forwards" }}
        />
        {isSuccess ? (
          <path
            d="M28 50 L42 64 L70 34"
            fill="none"
            stroke={color}
            strokeWidth="5"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="60"
            strokeDashoffset="60"
            style={{ animation: "markDraw .3s ease forwards .4s" }}
          />
        ) : (
          <>
            <line
              x1="34"
              y1="34"
              x2="62"
              y2="62"
              stroke={color}
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray="40"
              strokeDashoffset="40"
              style={{ animation: "markDraw .25s ease forwards .4s" }}
            />
            <line
              x1="62"
              y1="34"
              x2="34"
              y2="62"
              stroke={color}
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray="40"
              strokeDashoffset="40"
              style={{ animation: "markDraw .25s ease forwards .5s" }}
            />
          </>
        )}
      </svg>
      <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: 15 }}>{text}</Typography>
    </Box>
  );
}